import { OnInit, OnDestroy, ViewChild, Component } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { Subscription } from 'rxjs';

import { UiService } from '@services/ui.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  columnState,
  columnParamsLocalStorage,
  columnsParams,
} from '../models/types/types';
import { AuthService } from '@services/auth.service';

export type columnParams = {
  name: string;
  title: string;
  visible: boolean;
  editable: boolean;
  role?: string;
};

@Component({
  template: '',
})
export class DatatableBaseComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatSort, { static: true }) sort2: MatSort;
  private subscription = new Subscription();

  displayedColumns: string[];

  columnsParams: columnParams[] = [];

  isLoading: boolean;

  editedId: number;
  isCreationMode: boolean;

  localStorageColumnsParamsName: string = '';

  constructor(
    protected uiSrv: UiService,
    protected _authSrv?: AuthService,
  ) {}

  ngOnInit() {
    this.initBaseSubs();
  }

  onEdit(id?: number): void {
    this.isCreationMode = !id;
    this.editedId = id;
  }

  onEditFinished(): void {
    this.editedId = null;
    this.isCreationMode = false;
  }

  private initBaseSubs(): void {
    this.subscription.add(
      this.uiSrv.loadingStateChanged.subscribe((loadingState) => {
        this.isLoading = loadingState;
      }),
    );
  }

  protected addSubscription(newSub: Subscription): void {
    this.subscription.add(newSub);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onSelectColumnVisibility(columnParam: columnState): void {
    this.changeColumnVisibility(columnParam.name, columnParam.checked);
    this.saveColumnsParamsInLocalStorage();
    this.generateDisplayedColumns();
  }

  changeColumnVisibility(name: string, checked: boolean): void {
    const col = this.columnsParams.find(
      (columnParam) => columnParam.name === name,
    );

    if (col && col.editable) col.visible = checked;
  }

  onMooveColumnsOrder(event: CdkDragDrop<string[]>) {
    this.mooveColumnsOrder(event);
    this.saveColumnsParamsInLocalStorage();
    this.generateDisplayedColumns();
  }

  mooveColumnsOrder(event: CdkDragDrop<string[]>) {
    moveItemInArray(
      this.columnsParams,
      event.previousIndex,
      event.currentIndex,
    );
  }

  saveColumnsParamsInLocalStorage() {
    //keep only the parameters necessary for the localstorage
    const localStorageColumns: columnParamsLocalStorage[] =
      this.columnsParams.map((column) => {
        return {
          name: column.name,
          visible: column.visible,
        };
      });

    this.saveInLocalStorage(
      this.localStorageColumnsParamsName,
      localStorageColumns,
    );
  }

  readColumnsParamsFromLocalStorage() {
    const columnsFromLocalStorage: columnParamsLocalStorage[] =
      this.readFromLocalStorage<columnsParams>(
        this.localStorageColumnsParamsName,
      ) ?? [];

    //Update the actual columnsParams list with the params from localstorage
    this.columnsParams.forEach((column, index) => {
      const localStorageColumn = columnsFromLocalStorage.find(
        (lsColumn) => lsColumn.name === column.name,
      );

      if (localStorageColumn) {
        column.visible = localStorageColumn.visible;

        moveItemInArray(
          this.columnsParams,
          index,
          columnsFromLocalStorage.findIndex(
            (lsColumn) => lsColumn.name === column.name,
          ),
        );
      }
    });
  }

  generateDisplayedColumns(): void {
    this.displayedColumns = this.columnsParams
      .filter((columnParam) => {
        let roleValid = true;
        if (this._authSrv && columnParam.role)
          roleValid =
            this._authSrv.getDecodedToken().scope === columnParam.role;

        return columnParam.visible === true && roleValid;
      })
      .map((columnParam) => columnParam.name);
  }

  saveInLocalStorage(name: string, data: any) {
    localStorage.setItem(name, JSON.stringify(data));
  }

  readFromLocalStorage<T>(name: string): T | null {
    const datas = localStorage.getItem(name);
    return datas ? JSON.parse(datas) : null;
  }
}
